import { useState, useEffect } from 'react';
import type {
  CreateEntryRequest,
  UpdateEntryRequest,
  TimesheetEntryWithTaskCode,
} from '@renewal/types';
import { useTaskCodesForEmployee } from '../hooks/useTaskCodes.js';
import { useFunds } from '../hooks/useFunds.js';
import {
  fillerFundReason,
  fillerFundSuffix,
  fundTakesFiller,
  isFillerTaskCode,
} from '../utils/weekly-target.js';
import './EntryFormModal.css';

interface EntryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (entry: CreateEntryRequest | UpdateEntryRequest) => Promise<void>;
  entry?: TimesheetEntryWithTaskCode; // If editing existing entry
  date: string;
  employeeId: string;
  employeeAge: number;
  isSchoolDay: boolean;
}

/**
 * Calculate hours from start and end time strings.
 */
function calculateDisplayHours(startTime: string, endTime: string): number {
  if (!startTime || !endTime) return 0;

  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  const startTotalMinutes = (startHours ?? 0) * 60 + (startMinutes ?? 0);
  const endTotalMinutes = (endHours ?? 0) * 60 + (endMinutes ?? 0);

  if (endTotalMinutes <= startTotalMinutes) return 0;

  return (endTotalMinutes - startTotalMinutes) / 60;
}

/** "09:15" or "09:15:00" sits on :00, :15, :30 or :45 with no seconds. */
function isQuarterHourTime(time: string): boolean {
  return /^\d{2}:(00|15|30|45)(:00)?$/.test(time);
}

const QUARTER_HOUR_FIELD_ERROR = 'Use a quarter hour: :00, :15, :30 or :45.';

/**
 * Format date like "Monday, January 15, 2025"
 */
function formatFullDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

interface EntryFormData {
  taskCodeId: string;
  startTime: string;
  endTime: string;
  supervisorPresentName: string;
  mealBreakConfirmed: boolean | null;
  notes: string;
  fundId: string; // '' = General Fund (default), otherwise fund.id as string
}

interface FormErrors {
  taskCodeId?: string;
  startTime?: string;
  endTime?: string;
  supervisorPresentName?: string;
  mealBreakConfirmed?: string;
  notes?: string;
  fundId?: string;
  submit?: string;
}

export function EntryFormModal({
  isOpen,
  onClose,
  onSubmit,
  entry,
  date,
  employeeId,
  employeeAge,
  isSchoolDay,
}: EntryFormModalProps) {
  const { taskCodes, loading: loadingTasks } = useTaskCodesForEmployee(employeeId, date);
  // Editing needs every cached fund, so an entry whose fund has since closed
  // still shows that fund instead of silently reading "General Fund".
  const { funds, loading: loadingFunds } = useFunds({ includeAll: Boolean(entry) });
  const [fundNotice, setFundNotice] = useState<string | null>(null);
  const [formData, setFormData] = useState<EntryFormData>({
    taskCodeId: '',
    startTime: '',
    endTime: '',
    supervisorPresentName: '',
    mealBreakConfirmed: null,
    notes: '',
    fundId: '',
  });
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  // Initialize form data when entry changes (edit mode)
  useEffect(() => {
    if (entry) {
      setFormData({
        taskCodeId: entry.taskCodeId,
        startTime: entry.startTime.slice(0, 5),
        endTime: entry.endTime.slice(0, 5),
        supervisorPresentName: entry.supervisorPresentName || '',
        mealBreakConfirmed: entry.mealBreakConfirmed,
        notes: entry.notes || '',
        fundId: entry.fundId != null ? String(entry.fundId) : '',
      });
    } else {
      setFormData({
        taskCodeId: '',
        startTime: '',
        endTime: '',
        supervisorPresentName: '',
        mealBreakConfirmed: null,
        notes: '',
        fundId: '',
      });
    }
    setFieldErrors({});
    setFundNotice(null);
  }, [entry, isOpen]);

  if (!isOpen) return null;

  const selectedTask = taskCodes.find((tc) => tc.id === formData.taskCodeId);

  // Times saved before quarter hours were required stay editable: the 15-minute
  // step goes only on an input whose loaded time is already on a quarter mark,
  // and the quarter-hour check runs only once a time is changed. Then, as on
  // the server, both times must be on the quarter hour, the untouched one too.
  const loadedStart = entry?.startTime.slice(0, 5);
  const loadedEnd = entry?.endTime.slice(0, 5);
  const timesChanged = formData.startTime !== loadedStart || formData.endTime !== loadedEnd;
  const startStep = !entry || isQuarterHourTime(entry.startTime) ? 900 : undefined;
  const endStep = !entry || isQuarterHourTime(entry.endTime) ? 900 : undefined;

  // Funds: open funds, plus the entry's own fund even when it has closed. The
  // filler code (time not worked) cannot go on a restricted fund unless the
  // entry already sits there and neither its task code nor its fund changes.
  const currentFundId = entry?.fundId ?? null;
  const fundOptions = funds.filter((f) => f.selectable !== false || f.id === currentFundId);
  const currentFundMissing =
    currentFundId !== null && !fundOptions.some((f) => f.id === currentFundId);
  const fillerBlocks = (fund: (typeof funds)[number], taskCodeId: string): boolean => {
    const task = taskCodes.find((tc) => tc.id === taskCodeId);
    if (!isFillerTaskCode(task?.code) || fundTakesFiller(fund)) return false;
    const unchanged = !!entry && fund.id === currentFundId && taskCodeId === entry.taskCodeId;
    return !unchanged;
  };
  const isFiller = isFillerTaskCode(selectedTask?.code);

  const handleTaskChange = (taskCodeId: string) => {
    const fund = funds.find((f) => String(f.id) === formData.fundId);
    const task = taskCodes.find((tc) => tc.id === taskCodeId);
    if (task && fund && fillerBlocks(fund, taskCodeId)) {
      setFormData((prev) => ({ ...prev, taskCodeId, fundId: '' }));
      setFundNotice(
        `Funding source changed to General Fund: ${task.code} cannot go on ${fund.name}.`
      );
    } else {
      setFormData((prev) => ({ ...prev, taskCodeId }));
    }
  };

  const calculatedHours = calculateDisplayHours(formData.startTime, formData.endTime);
  const needsSupervisorAttestation =
    selectedTask &&
    (selectedTask.supervisorRequired === 'always' ||
      (selectedTask.supervisorRequired === 'for_minors' && employeeAge < 18));
  const needsMealBreakConfirmation = calculatedHours > 6 && employeeAge < 18;

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.taskCodeId) {
      newErrors.taskCodeId = 'Please select a task';
    }
    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }
    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }
    if (formData.startTime && formData.endTime && formData.startTime >= formData.endTime) {
      newErrors.endTime = 'End time must be after start time';
    }
    if (timesChanged && formData.startTime && !isQuarterHourTime(formData.startTime)) {
      newErrors.startTime = QUARTER_HOUR_FIELD_ERROR;
    }
    if (
      timesChanged &&
      !newErrors.endTime &&
      formData.endTime &&
      !isQuarterHourTime(formData.endTime)
    ) {
      newErrors.endTime = QUARTER_HOUR_FIELD_ERROR;
    }
    const chosenFund = funds.find((f) => String(f.id) === formData.fundId);
    if (selectedTask && chosenFund && fillerBlocks(chosenFund, formData.taskCodeId)) {
      newErrors.fundId = fillerFundReason(selectedTask.code, selectedTask.name);
    }
    if (needsSupervisorAttestation && !formData.supervisorPresentName.trim()) {
      newErrors.supervisorPresentName = 'Supervisor name is required for this task';
    }
    if (needsMealBreakConfirmation && formData.mealBreakConfirmed === null) {
      newErrors.mealBreakConfirmed = 'Please confirm if you took a meal break';
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);
    try {
      const parsedFundId = formData.fundId ? Number(formData.fundId) : null;
      const data: CreateEntryRequest | UpdateEntryRequest = entry
        ? {
            startTime: formData.startTime,
            endTime: formData.endTime,
            taskCodeId: formData.taskCodeId,
            supervisorPresentName: needsSupervisorAttestation
              ? formData.supervisorPresentName
              : null,
            mealBreakConfirmed: needsMealBreakConfirmation ? formData.mealBreakConfirmed : null,
            notes: formData.notes.trim() || null,
            fundId: parsedFundId,
          }
        : {
            workDate: date,
            taskCodeId: formData.taskCodeId,
            startTime: formData.startTime,
            endTime: formData.endTime,
            isSchoolDay,
            supervisorPresentName: needsSupervisorAttestation
              ? formData.supervisorPresentName
              : null,
            mealBreakConfirmed: needsMealBreakConfirmation ? formData.mealBreakConfirmed : null,
            notes: formData.notes.trim() || null,
            fundId: parsedFundId,
          };

      await onSubmit(data);
      onClose();
    } catch (err) {
      setFieldErrors({ submit: err instanceof Error ? err.message : 'Failed to save' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="entry-form-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="entry-form-modal"
      >
        <header className="modal-header">
          <h3>{entry ? 'Edit Entry' : 'Add Entry'}</h3>
          <span className="modal-date">{formatFullDate(date)}</span>
          <button
            className="close-button"
            onClick={onClose}
            aria-label="Close"
            data-testid="entry-form-close-button"
          >
            &times;
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          {fieldErrors.submit && (
            <div className="form-error" role="alert" data-testid="error-entry-form">
              {fieldErrors.submit}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="taskCodeId">Task *</label>
            {loadingTasks ? (
              <div className="loading-tasks">Loading tasks...</div>
            ) : (
              <select
                id="taskCodeId"
                value={formData.taskCodeId}
                onChange={(e) => handleTaskChange(e.target.value)}
                required
                data-testid="field-taskCodeId"
              >
                <option value="">Select a task...</option>
                {taskCodes.map((tc) => (
                  <option key={tc.id} value={tc.id}>
                    {tc.code} - {tc.name} (${tc.currentRate.toFixed(2)}/hr)
                  </option>
                ))}
              </select>
            )}
            {fieldErrors.taskCodeId && (
              <span className="field-error">{fieldErrors.taskCodeId}</span>
            )}
          </div>

          {funds.length > 0 && (
            <div className="form-group">
              <label htmlFor="fundId">Funding Source</label>
              {loadingFunds ? (
                <div className="loading-tasks">Loading funds...</div>
              ) : (
                <select
                  id="fundId"
                  value={formData.fundId}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, fundId: e.target.value }));
                    setFundNotice(null);
                  }}
                  aria-describedby={isFiller ? 'entry-filler-fund-reason' : undefined}
                  data-testid="field-fundId"
                >
                  <option value="">General Fund (Default)</option>
                  {fundOptions.map((fund) => {
                    const blocked = fillerBlocks(fund, formData.taskCodeId);
                    return (
                      <option key={fund.id} value={fund.id} disabled={blocked}>
                        {fund.name}
                        {fund.selectable === false ? ' (closed to new hours)' : ''}
                        {blocked ? fillerFundSuffix(fund) : ''}
                      </option>
                    );
                  })}
                  {currentFundMissing && (
                    <option value={String(currentFundId)}>
                      Current fund (no longer in the fund list)
                    </option>
                  )}
                </select>
              )}
              {isFiller && selectedTask && (
                <span
                  className="help-text"
                  id="entry-filler-fund-reason"
                  data-testid="entry-filler-fund-reason"
                >
                  {fillerFundReason(selectedTask.code, selectedTask.name)}
                </span>
              )}
              {fundNotice && (
                <span className="help-text" role="status" data-testid="entry-fund-notice">
                  {fundNotice}
                </span>
              )}
              {fieldErrors.fundId && (
                <span className="field-error" data-testid="error-field-fundId">
                  {fieldErrors.fundId}
                </span>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startTime">Start Time *</label>
              <input
                id="startTime"
                type="time"
                step={startStep}
                value={formData.startTime}
                onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))}
                required
                data-testid="field-startTime"
              />
              {fieldErrors.startTime && (
                <span className="field-error" data-testid="error-field-startTime">
                  {fieldErrors.startTime}
                </span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="endTime">End Time *</label>
              <input
                id="endTime"
                type="time"
                step={endStep}
                value={formData.endTime}
                onChange={(e) => setFormData((prev) => ({ ...prev, endTime: e.target.value }))}
                required
                data-testid="field-endTime"
              />
              {fieldErrors.endTime && (
                <span className="field-error" data-testid="error-field-endTime">
                  {fieldErrors.endTime}
                </span>
              )}
            </div>
          </div>

          <div className="calculated-hours">
            <span className="hours-label">Hours:</span>
            <span className="hours-value">{calculatedHours.toFixed(2)}</span>
          </div>

          {needsSupervisorAttestation && (
            <div className="form-group supervisor-field">
              <label htmlFor="supervisorPresentName">Supervisor Present *</label>
              <input
                id="supervisorPresentName"
                type="text"
                value={formData.supervisorPresentName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, supervisorPresentName: e.target.value }))
                }
                placeholder="Name of supervising adult"
                required
                data-testid="field-supervisorPresentName"
              />
              <span className="help-text">This task requires a supervisor to be present</span>
              {fieldErrors.supervisorPresentName && (
                <span className="field-error">{fieldErrors.supervisorPresentName}</span>
              )}
            </div>
          )}

          {needsMealBreakConfirmation && (
            <div className="form-group meal-break-field">
              <label>Meal Break Confirmation *</label>
              <div className="checkbox-field">
                <input
                  id="mealBreakConfirmed"
                  type="checkbox"
                  checked={formData.mealBreakConfirmed === true}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, mealBreakConfirmed: e.target.checked }))
                  }
                  data-testid="field-mealBreakConfirmed"
                />
                <label htmlFor="mealBreakConfirmed">
                  I confirm I took a 30-minute meal break during this shift
                </label>
              </div>
              <span className="help-text">Required for shifts over 6 hours (workers under 18)</span>
              {fieldErrors.mealBreakConfirmed && (
                <span className="field-error">{fieldErrors.mealBreakConfirmed}</span>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Optional — e.g., invoice reference, task description"
              maxLength={500}
              rows={2}
              data-testid="field-notes"
            />
            {formData.notes.length > 0 && (
              <span className="help-text">{formData.notes.length}/500</span>
            )}
            {fieldErrors.notes && <span className="field-error">{fieldErrors.notes}</span>}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={submitting}
              data-testid="entry-form-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting}
              data-testid="entry-form-submit-button"
            >
              {submitting ? 'Saving...' : entry ? 'Update' : 'Add Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
